import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiPhone, FiMapPin, FiSend, FiCheck, FiUsers, FiShield, FiTarget, FiHeart, FiGlobe, FiZap, FiBriefcase } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Static.css';

/* ==================== ABOUT US ==================== */
export const AboutPage = () => (
  <div className="static-page">
    <div className="container" style={{ maxWidth: '900px' }}>
      <div className="static-hero">
        <h1>About PlacementHunter</h1>
        <p>South Africa's dedicated employment platform — connecting talent with opportunity, without the noise.</p>
      </div>

      <div className="static-content">
        <section className="content-section">
          <h2>Our Mission</h2>
          <p>
            PlacementHunter was built with a single purpose: to make finding employment and hiring talent in South Africa 
            straightforward, efficient, and fair. Unlike social media platforms that mix job listings with personal updates 
            and viral content, we focus exclusively on connecting professionals with opportunities that match their skills 
            and career goals.
          </p>
          <p>
            We believe that the job search process should respect both candidates' time and recruiters' needs. Every feature 
            on our platform is designed to reduce friction, improve matching accuracy, and help South Africans find meaningful 
            employment faster.
          </p>
        </section>

        <section className="content-section">
          <h2>What Makes Us Different</h2>
          <div className="value-cards">
            <div className="value-card">
              <FiTarget className="value-icon" />
              <h3>Purpose-Built</h3>
              <p>We're not a social network with a jobs tab. Every feature serves one goal — connecting the right candidate with the right job.</p>
            </div>
            <div className="value-card">
              <FiShield className="value-icon" />
              <h3>POPIA Compliant</h3>
              <p>Your personal information is protected under South Africa's Protection of Personal Information Act. We take data privacy seriously.</p>
            </div>
            <div className="value-card">
              <FiZap className="value-icon" />
              <h3>Smart Matching</h3>
              <p>Our algorithm considers skills, location preferences, salary expectations, and experience level to surface the most relevant opportunities.</p>
            </div>
            <div className="value-card">
              <FiUsers className="value-icon" />
              <h3>Privacy Controls</h3>
              <p>Candidates can hide their profile from their current employer's recruiters, enabling a confidential job search when needed.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>Our Values</h2>
          <p>
            Transparency guides everything we do. Candidates see where their applications stand in real-time. Recruiters 
            see only applications relevant to their company. Everyone operates within a clear, permission-based system that 
            ensures fairness and prevents misuse.
          </p>
          <p>
            We're proudly South African, built to serve the local market with features and pricing that make sense for 
            businesses of all sizes — from SMEs in Soweto to corporates in Sandton.
          </p>
        </section>

        <section className="content-section">
          <h2>Our Team</h2>
          <p>
            PlacementHunter is developed by a team of South African technology professionals who understand the local 
            employment landscape. We combine deep technical expertise with practical recruitment industry knowledge to 
            build a platform that actually works for how hiring happens in South Africa.
          </p>
        </section>
      </div>
    </div>
  </div>
);

/* ==================== CONTACT US ==================== */
export const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    // Simulate send
    await new Promise(r => setTimeout(r, 1000));
    setSent(true);
    setSending(false);
    toast.success('Message sent! We\'ll get back to you shortly.');
  };

  return (
    <div className="static-page">
      <div className="container" style={{ maxWidth: '900px' }}>
        <div className="static-hero">
          <h1>Contact Us</h1>
          <p>Have questions? We'd love to hear from you. Get in touch with our team.</p>
        </div>

        <div className="contact-grid">
          <div className="contact-info">
            <div className="contact-item">
              <FiMail className="contact-icon" />
              <div>
                <h4>Email</h4>
                <p>support@placementhunter.co.za</p>
                <p className="sub">We respond within 24 hours</p>
              </div>
            </div>
            <div className="contact-item">
              <FiPhone className="contact-icon" />
              <div>
                <h4>Phone</h4>
                <p>+27 11 XXX XXXX</p>
                <p className="sub">Mon - Fri, 08:00 - 17:00 SAST</p>
              </div>
            </div>
            <div className="contact-item">
              <FiMapPin className="contact-icon" />
              <div>
                <h4>Office</h4>
                <p>Johannesburg, Gauteng</p>
                <p className="sub">South Africa</p>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: '2rem' }}>
            {sent ? (
              <div className="empty-state" style={{ padding: '2rem 0' }}>
                <div className="icon">✅</div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. Our team will respond within 24 hours.</p>
                <button className="btn btn-primary" onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}>Send Another</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="grid-2">
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Subject</label>
                  <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Message *</label>
                  <textarea rows="5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <button type="submit" className="btn btn-primary btn-lg" disabled={sending}>
                  {sending ? 'Sending...' : <><FiSend /> Send Message</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==================== CAREERS ==================== */
export const CareersPage = () => (
  <div className="static-page">
    <div className="container" style={{ maxWidth: '900px' }}>
      <div className="static-hero">
        <h1>Careers at PlacementHunter</h1>
        <p>Join the team that's reshaping how South Africa finds work.</p>
      </div>
      <div className="static-content">
        <section className="content-section">
          <h2>Why Work With Us</h2>
          <p>
            At PlacementHunter, you'll be part of a mission-driven team building technology that has a real impact on 
            people's lives. Every feature we ship helps someone find their next job or find the right person to join 
            their team. We value ownership, clear communication, and shipping work that matters.
          </p>
          <div className="value-cards" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="value-card"><FiHeart className="value-icon" /><h3>Meaningful Work</h3><p>Build products that directly help South Africans find employment and grow their careers.</p></div>
            <div className="value-card"><FiGlobe className="value-icon" /><h3>Remote-Friendly</h3><p>Work from anywhere in South Africa. We focus on output, not office hours.</p></div>
            <div className="value-card"><FiZap className="value-icon" /><h3>Growth</h3><p>Learning budget, conference attendance, and mentorship from experienced technologists.</p></div>
            <div className="value-card"><FiShield className="value-icon" /><h3>Benefits</h3><p>Competitive salary, medical aid contribution, and flexible leave policy.</p></div>
          </div>
        </section>

        <section className="content-section">
          <h2>Open Positions</h2>
          <div className="careers-list">
            {[
              { title: 'Senior Full-Stack Developer', team: 'Engineering', location: 'Remote (SA)', type: 'Full-time' },
              { title: 'Product Designer', team: 'Design', location: 'Johannesburg / Remote', type: 'Full-time' },
              { title: 'DevOps Engineer', team: 'Infrastructure', location: 'Remote (SA)', type: 'Full-time' },
              { title: 'Customer Success Manager', team: 'Operations', location: 'Johannesburg', type: 'Full-time' },
            ].map((job, i) => (
              <div key={i} className="career-item">
                <div>
                  <h4>{job.title}</h4>
                  <span className="career-meta">{job.team} · {job.location} · {job.type}</span>
                </div>
                <Link to="/contact" className="btn btn-secondary btn-sm">Apply</Link>
              </div>
            ))}
          </div>
          <p style={{ color: 'var(--text-muted)', marginTop: '1.5rem' }}>
            Don't see the right fit? Email your CV to <a href="mailto:careers@placementhunter.co.za">careers@placementhunter.co.za</a> — we're always looking for exceptional people.
          </p>
        </section>
      </div>
    </div>
  </div>
);

/* ==================== CAREER RESOURCES ==================== */
export const CareerResourcesPage = () => (
  <div className="static-page">
    <div className="container" style={{ maxWidth: '900px' }}>
      <div className="static-hero">
        <h1>Career Resources</h1>
        <p>Tips, guides, and advice to help you land your next role in South Africa.</p>
      </div>
      <div className="static-content">
        {[
          { title: 'Writing a CV That Stands Out', content: 'Your CV is your first impression. In the South African job market, recruiters spend an average of 7 seconds scanning a CV. Lead with a strong professional summary that states your title, years of experience, and key achievement. Tailor your CV for each application by mirroring keywords from the job description. Keep it to two pages maximum, use a clean format, and always include your location and availability. Avoid including personal information like your ID number — this isn\'t required and creates privacy risks.' },
          { title: 'Preparing for Interviews', content: 'Research the company thoroughly before your interview. Understand their products, market position, and recent news. Prepare STAR responses (Situation, Task, Action, Result) for behavioural questions. For technical roles, practice on platforms like LeetCode or HackerRank. South African companies increasingly use video interviews, so test your equipment beforehand, choose a quiet space with good lighting, and dress professionally from head to toe.' },
          { title: 'Salary Negotiation in South Africa', content: 'Know your market value before entering negotiations. Use platforms like PayScale, Glassdoor, and recruitment agency surveys to benchmark salaries for your role and experience level in your region. Johannesburg and Cape Town typically offer higher salaries than other cities. When asked for your expectations, provide a range rather than a single figure. Consider the total package including medical aid, retirement contributions, leave days, and remote work options.' },
          { title: 'Networking Effectively', content: 'In South Africa, many positions are filled through referrals before they\'re publicly posted. Attend industry events, join professional associations, and engage meaningfully on LinkedIn. Build relationships before you need them — offer help and insights to your network without expecting immediate returns. When reaching out to someone you don\'t know, keep your message brief, specific, and respectful of their time.' },
          { title: 'Understanding Your Employment Rights', content: 'South African employees are protected by the Basic Conditions of Employment Act, the Labour Relations Act, and the Employment Equity Act. Know your rights regarding working hours (maximum 45 per week), overtime pay, leave entitlements (at least 21 consecutive days annually), and notice periods. If you believe your rights have been violated, contact the CCMA (Commission for Conciliation, Mediation and Arbitration) for free assistance.' },
        ].map((resource, i) => (
          <section className="content-section" key={i}>
            <h2>{resource.title}</h2>
            <p>{resource.content}</p>
          </section>
        ))}
      </div>
    </div>
  </div>
);

/* ==================== PRICING ==================== */
export const PricingPage = () => (
  <div className="static-page">
    <div className="container" style={{ maxWidth: '1000px' }}>
      <div className="static-hero">
        <h1>Pricing</h1>
        <p>Transparent pricing designed for South African businesses of all sizes.</p>
      </div>

      <div className="pricing-grid">
        {[
          { name: 'Starter', price: 'Free', period: '', desc: 'Perfect for small businesses posting occasionally', features: ['Post up to 2 active jobs', 'View up to 25 applicants', 'Basic company profile', 'Email support', 'Standard job listing'], cta: 'Get Started', highlight: false },
          { name: 'Professional', price: 'R1,499', period: '/month', desc: 'For growing companies with regular hiring needs', features: ['Post up to 10 active jobs', 'Unlimited applicant views', 'Featured company profile', 'Candidate matching algorithm', 'Application pipeline tools', 'Priority support', 'Job performance analytics'], cta: 'Start Free Trial', highlight: true },
          { name: 'Enterprise', price: 'Custom', period: '', desc: 'For large organisations with high-volume recruitment', features: ['Unlimited job postings', 'Dedicated account manager', 'API access for ATS integration', 'Custom branding & career page', 'Advanced analytics & reporting', 'Bulk candidate messaging', 'SLA-backed support'], cta: 'Contact Sales', highlight: false },
        ].map((plan, i) => (
          <div key={i} className={`pricing-card ${plan.highlight ? 'featured' : ''}`}>
            {plan.highlight && <div className="pricing-badge">Most Popular</div>}
            <h3>{plan.name}</h3>
            <div className="pricing-price">{plan.price}<span>{plan.period}</span></div>
            <p className="pricing-desc">{plan.desc}</p>
            <ul className="pricing-features">
              {plan.features.map((f, j) => <li key={j}><FiCheck /> {f}</li>)}
            </ul>
            <Link to={plan.name === 'Enterprise' ? '/contact' : '/register?role=recruiter'} className={`btn ${plan.highlight ? 'btn-primary' : 'btn-secondary'} btn-block`}>
              {plan.cta}
            </Link>
          </div>
        ))}
      </div>

      <div className="static-content" style={{ textAlign: 'center', marginTop: '3rem' }}>
        <p style={{ color: 'var(--text-muted)' }}>All prices in ZAR (South African Rand), excl. VAT. Annual billing available with 20% discount.</p>
      </div>
    </div>
  </div>
);

/* ==================== RECRUITMENT SOLUTIONS ==================== */
export const RecruitmentSolutionsPage = () => (
  <div className="static-page">
    <div className="container" style={{ maxWidth: '900px' }}>
      <div className="static-hero">
        <h1>Recruitment Solutions</h1>
        <p>Comprehensive tools to streamline your hiring process from job posting to placement.</p>
      </div>
      <div className="static-content">
        <section className="content-section">
          <h2>End-to-End Recruitment Platform</h2>
          <p>
            PlacementHunter gives your recruitment team everything they need to find, evaluate, and hire the best 
            talent in South Africa. Our platform integrates job posting, candidate management, and communication 
            into a single workflow that saves time and reduces cost-per-hire.
          </p>
        </section>

        <div className="value-cards" style={{ gridTemplateColumns: 'repeat(2, 1fr)', margin: '2rem 0' }}>
          <div className="value-card"><FiBriefcase className="value-icon" /><h3>Job Management</h3><p>Post jobs with detailed descriptions, requirements, and salary ranges. Manage multiple active listings across different departments and locations from a single dashboard.</p></div>
          <div className="value-card"><FiTarget className="value-icon" /><h3>Smart Matching</h3><p>Our matching algorithm analyses candidate skills, experience, location, and salary expectations to surface the most suitable applicants for each role automatically.</p></div>
          <div className="value-card"><FiUsers className="value-icon" /><h3>Application Pipeline</h3><p>Track every application through customisable stages — from applied to reviewing, shortlisted, interview, offered, or rejected. Move candidates through your workflow efficiently.</p></div>
          <div className="value-card"><FiShield className="value-icon" /><h3>Compliance Built In</h3><p>POPIA-compliant data handling, role-based access control ensuring recruiters only see their company's applications, and full audit trails for every action.</p></div>
        </div>

        <section className="content-section">
          <h2>Ready to Transform Your Hiring?</h2>
          <p>
            Whether you're a startup making your first hire or an enterprise filling hundreds of positions, 
            PlacementHunter scales with your needs.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <Link to="/register?role=recruiter" className="btn btn-primary">Start Free Trial</Link>
            <Link to="/contact" className="btn btn-secondary">Talk to Sales</Link>
          </div>
        </section>
      </div>
    </div>
  </div>
);

/* ==================== POST A JOB (redirect info) ==================== */
export const PostJobPage = () => (
  <div className="static-page">
    <div className="container" style={{ maxWidth: '700px', textAlign: 'center' }}>
      <div className="static-hero">
        <h1>Post a Job</h1>
        <p>Reach thousands of qualified South African professionals.</p>
      </div>
      <div className="card" style={{ padding: '2.5rem' }}>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.7 }}>
          To post a job on PlacementHunter, you'll need a recruiter account. Registration is free and takes 
          less than two minutes. Once registered, you'll have access to our full suite of hiring tools including 
          job posting, candidate management, and smart matching.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <Link to="/register?role=recruiter" className="btn btn-primary btn-lg">Create Recruiter Account</Link>
          <Link to="/login" className="btn btn-secondary btn-lg">Sign In</Link>
        </div>
      </div>
    </div>
  </div>
);

/* ==================== NOTIFICATIONS ==================== */
export const NotificationsPage = () => (
  <div className="static-page">
    <div className="container" style={{ maxWidth: '700px' }}>
      <div className="page-header"><h1>Notifications</h1></div>
      <div className="empty-state"><div className="icon">🔔</div><h3>No notifications</h3><p>You're all caught up! Notifications about applications, messages, and job matches will appear here.</p></div>
    </div>
  </div>
);

/* ==================== SETTINGS ==================== */
export const SettingsPage = () => (
  <div className="static-page">
    <div className="container" style={{ maxWidth: '700px' }}>
      <div className="page-header"><h1>Settings</h1></div>
      <div className="card" style={{ padding: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>Account Settings</h3>
        <p style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
          Account settings management is coming soon. In the meantime, you can update your personal information 
          from the <Link to="/profile">Profile</Link> page, or contact our <Link to="/contact">support team</Link> for 
          account-related changes like email updates or account deletion requests.
        </p>
      </div>
    </div>
  </div>
);
